import { useState, useEffect, useRef, useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { observer, inject } from "mobx-react";
import { useTranslations } from 'next-intl';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import Product from './Product';
import MobilePrice from "./mobile/MobilePrice";
import MobilePriceInfo from "./mobile/MobilePriceInfo";
import MobileVariationSelect from "./mobile/MobileVariationSelect";
import MobileBulkSalePrice from "./mobile/MobileBulkSalePrice";
import QuantityControls from "./QuantityControls";
import QuantityIndicator from "./QuantityIndicator";
import Tags from "./Tags";
import AddToCart from './AddToCart';
import ProductImage from './ProductImage';
import FavoriteButton from "./FavoriteButton";
import { getPriceDetails, getPerUnit, getPerUnitDisplay } from '@/utils/misc';

const ProductView = inject('wooCommerceStore')(observer(({
  product,
  inCart,
  img,
  handleClick,
  selectedVariation,
  setSelectedVariation,
  handleRemoveFromCart,
  handleAddToCart,
  handleClearFromCart,
  handleUpdateQuantity,
  productWeightQuantity,
  quantity,
  handleVariationChange,
  crosssellProduct,
  forceCard,
  studioStore,
  isFavoriteView,
  wooCommerceStore,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isImageHovered, setIsImageHovered] = useState(false);
  const [isRTL, setIsRTL] = useState(false);
  const [offset, setOffset] = useState(0);
  const primaryColorRef = useRef();
  const hoverTimerRef = useRef(null);
  const { isMobile } = useWindowDimensions();
  const tu = useTranslations('Units');
  const weightUnit = wooCommerceStore.weightUnit || 'kg';

  useEffect(() => {
    if (studioStore && studioStore.settings && studioStore.settings.colors) {
      primaryColorRef.current = studioStore.settings.colors.primary_color;
    }
  }, [studioStore]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Detect RTL
      setIsRTL(document.documentElement.dir === 'rtl');

      // Calculate the 2rem offset in pixels and store it
      const remToPx = parseFloat(getComputedStyle(document.documentElement).fontSize) * 2;
      setOffset(remToPx);
    }
  }, []);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
      }
    };
  }, []);

  const handleTouchStart = () => {
    // Clear any existing timer
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
    }

    // Show the overlay
    setIsImageHovered(true);

    // Start a new 5-second timer
    hoverTimerRef.current = setTimeout(() => {
      setIsImageHovered(false);
    }, 5000);
  };

  const slideInVariants = {
    hidden: { x: isRTL ? '-100%' : '100%', opacity: 0 },
    visible: { x: isRTL ? `-${offset - 5}px` : `${offset - 5}px`, opacity: 1 },
    exit: { x: isRTL ? '-100%' : '100%', opacity: 0 },
  };

  // Memoize price details calculation
  const priceDetails = useMemo(() => {
    const currentProduct = selectedVariation || product;
    const productData = {
      id: currentProduct?.id,
      regular_price: currentProduct?.regular_price,
      sale_price: currentProduct?.sale_price,
      weight: currentProduct?.weight,
      weightable: currentProduct?.weightable,
      units_based: currentProduct?.units_based,
      pack_based: currentProduct?.pack_based,
      short_description: currentProduct?.short_description,
      name: currentProduct?.name,
      milliliter: currentProduct?.milliliter,
      show_price_per_100g: currentProduct?.show_price_per_100g,
    };

    const details = getPriceDetails(productData, weightUnit, tu, product?.type);
    details.variant = "mobile";
    details.isWeightBased = currentProduct?.weightable && currentProduct?.weight > 0 && currentProduct?.regular_price > 0;
    details.isTypeBased = !currentProduct?.weightable && (currentProduct?.units_based || currentProduct?.pack_based);
    return details;
  }, [selectedVariation, product, weightUnit, tu]);

  // Memoize the product display type calculation
  const productDisplayType = useMemo(() => {
    const currentProduct = selectedVariation || product;
    return getPerUnit({
      perUnit: currentProduct?.per_unit,
      weightUnit,
      tu,
    });
  }, [selectedVariation, product, weightUnit, tu]);

  // Memoize product classes
  const productClasses = useMemo(() => {
    const baseClasses = ["product-card", "h-full", "bg-white"];

    if (studioStore?.products) {
      const borderClasses = [
        studioStore.products.border_width.border_width_top,
        studioStore.products.border_width.border_width_left,
        studioStore.products.border_width.border_width_right,
        studioStore.products.border_width.border_width_bottom,
        studioStore.products.border_radius,
      ].filter(Boolean);

      return [...baseClasses, ...borderClasses];
    }

    return baseClasses;
  }, [studioStore?.products]);

  const productPerUnitDisplay = useMemo(() => {
    const currentProduct = selectedVariation || product;
    return getPerUnitDisplay({
      perUnit: currentProduct?.per_unit_display,
      weightUnit,
      tu,
    });
  }, [selectedVariation, product, weightUnit, tu]);

  const renderProductContent = () => (
    <div className={`flex ${forceCard ? 'flex-col space-x-0 p-0 flex-nowrap' : 'flex-row space-x-2 p-2 flex-wrap'} md:flex-col h-full items-center md:space-x-0 md:p-0 md:flex-nowrap`}>
      <div
        data-testid={`product-image-container-${product.id}`}
        className="relative shrink-0 md:w-auto md:pt-2 md:px-2 max-width-[100px] md:max-w-full"
        onMouseEnter={() => setIsImageHovered(true)}
        onMouseLeave={() => setIsImageHovered(false)}
        onTouchStart={handleTouchStart}
      >
        {!isMobile && <Tags onSaleOnly={product.on_sale} tags={product.tags} />}

        {/* Favorite Button */}
        <div className={`absolute ${forceCard ? 'top-2' : '-bottom-1'} md:top-2 left-0 md:left-2 rtl:right-0 md:rtl:right-2 rtl:left-auto z-10 w-6 h-6`}>
          <FavoriteButton product={product} isFavoriteView={isFavoriteView} size="26" colorSchema="light" />
        </div>

        <div className="relative w-full">
          <ProductImage
            img={img}
            productName={product?.name}
            className="mx-auto"
            width={isMobile && !forceCard ? 100 : 250}
            height={isMobile && !forceCard ? 80 : 175}
          />
          <AnimatePresence>
            {isImageHovered && !isMobile && (
              <motion.div
                data-testid={`add-to-cart-overlay-${product.id}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="absolute inset-0 hidden md:flex flex-col items-center justify-center z-30"
              >
                <div
                  className={`absolute stamp-border stamp-border-sm inset-0 z-[-1] ${!primaryColorRef.current && 'bg-gray-500'}`}
                  style={{
                    backgroundImage: `radial-gradient(4px, rgba(255, 255, 255, 0) 98%, ${primaryColorRef.current}), linear-gradient(${primaryColorRef.current} 0 0)`,
                    opacity: 0.50,
                  }}
                />
                <div data-testid={`add-to-cart-container-${product.id}`} className="relative z-10">
                  <AddToCart
                    productVariations={product?.variations}
                    productId={product?.id}
                    productStockStatus={product?.stock_status}
                    handleRemoveFromCart={handleRemoveFromCart}
                    handleAddToCart={handleAddToCart}
                    handleUpdateQuantity={handleUpdateQuantity}
                    quantity={quantity}
                    productWeightQuantity={productWeightQuantity}
                    handleVariationChange={handleVariationChange}
                    productType={product?.type}
                    selectedVariation={selectedVariation}
                    setSelectedVariation={setSelectedVariation}
                    productDisplayType={productPerUnitDisplay}
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      <div className={`product-info flex ${forceCard ? 'text-center' : 'flex-1'} flex-col flex-1 px-2 md:p-3 md:p-5 cursor-pointer w-full mx-0 md:text-center`}>
        <div className={`flex ${forceCard ? 'flex-col' : 'flex-row'} md:flex-col justify-between`}>
          <div onClick={handleClick}>
            <h3 className="text-base leading-none text-lg font-bold text-heading cursor-pointer font-secondary text-primary-studio">
              {product.name}
            </h3>
            {!isMobile && <hr className="dotted-spaced hidden md:block my-3" />}
            <MobilePrice priceDetails={priceDetails} productDisplayType={productDisplayType} />
          </div>
          {isMobile && (
            <MobileVariationSelect
              productType={product?.type}
              product={product}
              selectedVariation={selectedVariation}
              setSelectedVariation={setSelectedVariation}
              handleVariationChange={handleVariationChange}
              productDisplayType={productPerUnitDisplay}
            />
          )}
        </div>
        {isMobile && <hr className="dotted-spaced my-1 md:hidden" />}
        <div className={`flex ${forceCard ? 'flex-col' : 'flex-row'} md:flex-col justify-between`}>
          <div onClick={handleClick}>
            <MobilePriceInfo priceDetails={priceDetails} />
            <MobileBulkSalePrice
              id={(selectedVariation || product)?.id}
              regularPrice={(selectedVariation || product)?.regular_price}
              bulkDiscountRules={(selectedVariation || product)?.bulk_discount_rules}
            />
          </div>
          {((product?.type === "variable" && selectedVariation) || product?.type === "simple") && isMobile && (
            <div className={`${forceCard ? 'mx-auto' : ''}`}>
              <QuantityControls
                handleRemoveFromCart={handleRemoveFromCart}
                handleAddToCart={handleAddToCart}
                handleUpdateQuantity={handleUpdateQuantity}
                quantity={quantity}
                productWeightQuantity={productWeightQuantity}
                productDisplayType={productDisplayType}
                productId={product.id}
                productStockStatus={product.stock_status}
                variant="mobile"
                disableInput
              />
            </div>
          )}
        </div>
      </div>
      {!!inCart && (
        <div className={`absolute ${forceCard ? 'bottom-0 right-0 rtl:left-0 rtl:right-auto' : 'top-0 left-0 rtl:right-0 rtl:left-auto'} md:bottom-0 md:top-auto md:right-0 md:rtl:left-0 md:rtl:right-auto p-3 md:p-4`}>
          <QuantityIndicator inCart={inCart} handleClearFromCart={handleClearFromCart} />
        </div>
      )}
      {(product?.tags?.length > 0 || product.on_sale) && isMobile && (
        <div className="flex flex-col flex-[1 1 50%] w-full">
          <Tags onSaleOnly={product.on_sale} tags={product.tags} side="right" className="mb-0" />
        </div>
      )}
    </div>
  );

  return (
    <div
      className="product-wrapper relative z-10 mb-3 md:mb-0 h-full bg-transparent"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        data-testid={`product-card-${product.id}`}
        className={[
          ...productClasses,
        ].join(' ') + ` relative z-20 overflow-hidden transition-shadow duration-200 ${isHovered ? 'md:shadow-lg' : 'shadow-none'} ${selectedVariation ? 'variation-' + selectedVariation.id : 'product-' + product.id}`}
      >
        {renderProductContent()}
      </div>

      <AnimatePresence>
        {!!inCart && crosssellProduct && isHovered && (
          <motion.div
            key="crosssell"
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={slideInVariants}
            transition={{ type: "tween", duration: 0.4 }}
            className="absolute top-0 right-full rtl:left-full rtl:right-auto w-[233px] h-full border border-yellow-400 rounded-md bg-white z-0"
          >
            <Product product={crosssellProduct} view="crosssell" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}));

export default ProductView;
